import React, { useEffect, useState } from 'react';
import { API_URL, CLOUD_CDN_BASE_URL } from '../../utils/constants';
import { getMentionedUsernames } from '../../utils/mentionUtils';
import './AISummary.css';

const AISummary = ({ query, recommendations, isVisible, onAddToList, hideActions = false, showAddToList = true }) => {
    const [summary, setSummary] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [mentionedUserProfilePhoto, setMentionedUserProfilePhoto] = useState(null);

    useEffect(() => {
        if (isVisible && query && !summary) {
            generateSummary();
        }
    }, [isVisible, query, recommendations]);

    // Fetch mentioned user's profile photo when we have a mention query
    useEffect(() => {
        if (isSpecificUserSearch() && query) {
            fetchMentionedUserProfilePhoto();
        }
    }, [query, recommendations]);

    // Function to fetch the mentioned user's profile photo
    const fetchMentionedUserProfilePhoto = async () => {
        try {
            // Extract the mentioned username from the query
            const mentionedUsernames = getMentionedUsernames(query);
            if (mentionedUsernames.length === 0) return;
            
            const mentionedUsername = mentionedUsernames[0]; // Take the first mentioned user
            console.log('=== DEBUG: Fetching profile photo for mentioned user ===');
            console.log('Mentioned username:', mentionedUsername);
            
            // Fetch the mentioned user's profile photo from the backend
            const response = await fetch(`${API_URL}/api/users/profile-photo?username=${encodeURIComponent(mentionedUsername)}`);
            
            if (response.ok) {
                const data = await response.json();
                if (data.success && data.profilePhoto) {
                    console.log('=== DEBUG: Got profile photo for mentioned user ===');
                    console.log('Profile photo:', data.profilePhoto);
                    setMentionedUserProfilePhoto(data.profilePhoto);
                } else {
                    console.log('=== DEBUG: No profile photo found for mentioned user ===');
                    console.log('Response:', data);
                }
            } else {
                console.log('=== DEBUG: Failed to fetch mentioned user profile photo ===');
                console.log('Status:', response.status);
            }
        } catch (error) {
            console.error('Error fetching mentioned user profile photo:', error);
        }
    };

    // Helper function to get image source (same logic as RecommendationCard)
    const getImageSrc = (photo) => {
        console.log('=== DEBUG: getImageSrc called with ===');
        console.log('Photo input:', photo);
        console.log('Photo type:', typeof photo);
        
        if (!photo) {
            console.log('getImageSrc: No photo provided, returning null');
            return null;
        }
        
        if (typeof photo === 'string') {
            // If it's already a full URL (starts with http), return it as is
            if (photo.startsWith('http')) {
                console.log('getImageSrc: Returning full URL as-is:', photo);
                return photo;
            }
            
            // Check if it's a GCS path for profile images
            if (photo.startsWith('profile_images/') || photo.startsWith('profiles/')) {
                const gcsUrl = `https://storage.googleapis.com/tried-and-trusted-images-production/${photo}`;
                console.log('getImageSrc: Constructed GCS URL:', gcsUrl);
                return gcsUrl;
            }
            
            // If it's a relative path, try to construct CDN URL
            if (photo.includes('/')) {
                const cdnUrl = `${CLOUD_CDN_BASE_URL}/${photo}`;
                console.log('getImageSrc: Constructed CDN URL:', cdnUrl);
                return cdnUrl;
            }
            
            console.log('getImageSrc: Returning string as-is:', photo);
            return photo;
        }
        
        if (photo.url) {
            console.log('getImageSrc: Returning photo.url:', photo.url);
            return photo.url;
        }
        
        if (photo.gcs_path) {
            // For GCS paths, construct the direct GCS URL
            const gcsUrl = `https://storage.googleapis.com/tried-and-trusted-images-production/${photo.gcs_path}`;
            console.log('getImageSrc: Constructed GCS URL from gcs_path:', gcsUrl);
            return gcsUrl;
        }
        
        if (photo.data && photo.contentType) {
            const bufferData = photo.data.data;
            if (!bufferData) {
                console.log('getImageSrc: No buffer data, returning null');
                return null;
            }
            try {
                const base64String = btoa(
                    new Uint8Array(bufferData).reduce(
                        (data, byte) => data + String.fromCharCode(byte),
                        ''
                    )
                );
                const dataUrl = `data:${photo.contentType};base64,${base64String}`;
                console.log('getImageSrc: Constructed data URL (truncated):', dataUrl.substring(0, 50) + '...');
                return dataUrl;
            } catch (e) {
                console.error("Error converting image buffer for display:", e);
                return null;
            }
        }
        
        console.log('getImageSrc: No valid image format found, returning null');
        return null;
    };

    // Get photos from top recommendations for display
    const getPhotosForDisplay = () => {
        if (!recommendations || recommendations.length === 0) return [];
        
        const photos = [];
        const topRecommendations = recommendations.slice(0, 3);
        
        topRecommendations.forEach(rec => {
            if (rec.images && Array.isArray(rec.images) && rec.images.length > 0) {
                // Get the first image from each recommendation
                const firstImage = rec.images[0];
                const imageSrc = getImageSrc(firstImage);
                if (imageSrc) {
                    photos.push({
                        src: imageSrc,
                        businessName: rec.business_name || rec.name || 'Business',
                        alt: `${rec.business_name || rec.name} photo`,
                        providerId: rec.provider_id || rec.id
                    });
                }
            }
        });
        
        return photos;
    };

    // Get profile photos of all users who have recommendations
    const getUsersWithRecommendations = () => {
        if (!recommendations || recommendations.length === 0) return [];
        
        const users = new Map();
        
        // Debug: Log the first recommendation to see what fields we have
        if (recommendations.length > 0) {
            console.log('=== DEBUG: First recommendation data ===');
            console.log('Available fields:', Object.keys(recommendations[0]));
            console.log('Profile-related fields:', Object.keys(recommendations[0]).filter(key => 
                key.includes('profile') || key.includes('image') || key.includes('photo')
            ));
            console.log('Full first recommendation:', recommendations[0]);
        }
        
        recommendations.forEach(rec => {
            // Get the recommender's information
            let recommenderId = null;
            let recommenderName = null;
            let recommenderPhoto = null;
            
            // Check different possible fields for recommender info
            if (rec.users_who_reviewed && rec.users_who_reviewed.length > 0) {
                // Use the first user who reviewed
                recommenderName = rec.users_who_reviewed[0];
                // Try to find profile photo from various fields, prioritizing profile_image_gcs_path
                recommenderPhoto = rec.profile_image_gcs_path || 
                                  rec.recommender_profile_image || 
                                  rec.recommender_profile_image_url || 
                                  rec.user_profile_image || 
                                  rec.user_profile_image_url ||
                                  rec.reviewer_profile_image || 
                                  rec.reviewer_profile_image_url;
                
                console.log(`=== DEBUG: User "${recommenderName}" profile photo search ===`);
                console.log('profile_image_gcs_path:', rec.profile_image_gcs_path);
                console.log('recommender_profile_image:', rec.recommender_profile_image);
                console.log('recommender_profile_image_url:', rec.recommender_profile_image_url);
                console.log('user_profile_image:', rec.user_profile_image);
                console.log('user_profile_image_url:', rec.user_profile_image_url);
                console.log('reviewer_profile_image:', rec.reviewer_profile_image);
                console.log('reviewer_profile_image_url:', rec.reviewer_profile_image_url);
                console.log('Final selected photo:', recommenderPhoto);
            } else if (rec.recommender_id) {
                recommenderId = rec.recommender_id;
                recommenderName = rec.recommender_name || rec.recommender_first_name;
                recommenderPhoto = rec.profile_image_gcs_path || 
                                  rec.recommender_profile_image || 
                                  rec.recommender_profile_image_url;
            } else if (rec.user_id) {
                recommenderId = rec.user_id;
                recommenderName = rec.user_name || rec.user_first_name;
                recommenderPhoto = rec.profile_image_gcs_path || 
                                  rec.user_profile_image || 
                                  rec.user_profile_image_url;
            } else if (rec.reviewer_id) {
                recommenderId = rec.reviewer_id;
                recommenderName = rec.reviewer_name || rec.reviewer_first_name;
                recommenderPhoto = rec.profile_image_gcs_path || 
                                  rec.reviewer_profile_image || 
                                  rec.reviewer_profile_image_url;
            }
            
            // If we have a recommender and haven't seen them before, add them
            if (recommenderName && !users.has(recommenderName)) {
                users.set(recommenderName, {
                    id: recommenderName, // Use name as ID since we don't have a proper ID
                    name: recommenderName,
                    photo: recommenderPhoto
                });
                
                console.log(`=== DEBUG: Added user "${recommenderName}" ===`);
                console.log('Photo value:', recommenderPhoto);
                console.log('Photo type:', typeof recommenderPhoto);
            }
        });
        
        console.log('=== DEBUG: Final users with recommendations ===');
        console.log(Array.from(users.values()));
        return Array.from(users.values());
    };

    // Check if the search query mentions a specific user
    const isSpecificUserSearch = () => {
        if (!query) return false;
        // Check if query contains @username pattern or mentions a specific person
        const isSpecific = query.includes('@') || query.toLowerCase().includes('venkat') || query.toLowerCase().includes('sarah') || query.toLowerCase().includes('mike') || query.toLowerCase().includes('john');
        return isSpecific;
    };

    // Get individual recommendation data with recommender info
    const getIndividualRecommendations = () => {
        if (!recommendations || recommendations.length === 0) return [];
        
        const topRecommendations = recommendations.slice(0, 3);
        
        return topRecommendations.map(rec => {
            // Get the recommender's information
            let recommenderId = null;
            let recommenderName = null;
            let recommenderPhoto = null;
            
            // Check different possible fields for recommender info
            if (rec.users_who_reviewed && rec.users_who_reviewed.length > 0) {
                // Use the first user who reviewed
                recommenderName = rec.users_who_reviewed[0];
                // Try to find profile photo from various fields, prioritizing profile_image_gcs_path
                recommenderPhoto = rec.profile_image_gcs_path || 
                                  rec.recommender_profile_image || 
                                  rec.recommender_profile_image_url || 
                                  rec.user_profile_image || 
                                  rec.user_profile_image_url ||
                                  rec.reviewer_profile_image || 
                                  rec.reviewer_profile_image_url;
            } else if (rec.recommender_id) {
                recommenderId = rec.recommender_id;
                recommenderName = rec.recommender_name || rec.recommender_first_name;
                recommenderPhoto = rec.profile_image_gcs_path || 
                                  rec.recommender_profile_image || 
                                  rec.recommender_profile_image_url;
            } else if (rec.user_id) {
                recommenderId = rec.user_id;
                recommenderName = rec.user_name || rec.user_first_name;
                recommenderPhoto = rec.profile_image_gcs_path || 
                                  rec.user_profile_image || 
                                  rec.user_profile_image_url;
            } else if (rec.reviewer_id) {
                recommenderId = rec.reviewer_id;
                recommenderName = rec.reviewer_name || rec.reviewer_first_name;
                recommenderPhoto = rec.profile_image_gcs_path || 
                                  rec.reviewer_profile_image || 
                                  rec.reviewer_profile_image_url;
            }
            
            return {
                ...rec,
                recommenderId,
                recommenderName: recommenderName || 'Anonymous',
                recommenderPhoto
            };
        });
    };

    // Function to make business names clickable
    const makeBusinessNamesClickable = (text, recommendations) => {
        if (!recommendations || recommendations.length === 0) return text;
        
        let linkedText = text;
        const topRecommendations = recommendations.slice(0, 3);
        
        topRecommendations.forEach(rec => {
            const businessName = rec.business_name || rec.name;
            if (businessName) {
                const providerId = rec.provider_id || rec.id;
                if (providerId) {
                    const regex = new RegExp(`(${businessName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
                    linkedText = linkedText.replace(regex, `<a href="/provider/${providerId}" class="ai-summary-business-link">$1</a>`);
                }
            }
        });
        
        return linkedText;
    };

    // Function to render text with clickable links
    const renderTextWithLinks = (text) => {
        if (!text) return null;
        
        const linkedText = makeBusinessNamesClickable(text, recommendations);
        
        // Split by line breaks and render each line
        const lines = linkedText.split('\n');
        
        return lines.map((line, index) => {
            if (line.includes('<a href=')) {
                // Line contains HTML links
                return (
                    <div 
                        key={index} 
                        dangerouslySetInnerHTML={{ __html: line }}
                        className="ai-summary-line"
                    />
                );
            } else {
                // Regular text line
                return (
                    <div key={index} className="ai-summary-line">
                        {line}
                    </div>
                );
            }
        });
    };

    const generateSummary = async () => {
        if (!query) return;

        setIsLoading(true);
        setError(null); // Clear any previous errors

        try {
            // If no recommendations, still generate a helpful AI response
            if (!recommendations || recommendations.length === 0) {
                const response = await fetch(`${API_URL}/api/ai-summary`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        query,
                        recommendations: [],
                        instructions: "The user is searching for recommendations but no specific recommendations were found. Provide a brief, direct response suggesting they ask their network or try different search terms. Keep it simple and helpful without over-explaining."
                    })
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const data = await response.json();
                
                if (data.success) {
                    setSummary(data.summary);
                } else {
                    throw new Error(data.error || 'Failed to generate summary');
                }
                return;
            }

            // Cap recommendations at top 3 for the AI summary
            const topRecommendations = recommendations.slice(0, 3);
            
            // Prepare recommendations data for the API
            const recsForAPI = topRecommendations.map(rec => {
                // Get the most relevant personal message
                let personalMessage = '';
                if (rec.recommender_message) {
                    personalMessage = rec.recommender_message;
                } else if (rec.review) {
                    personalMessage = rec.review;
                } else if (rec.content) {
                    personalMessage = rec.content;
                }

                return {
                    business_name: rec.business_name || rec.name,
                    average_rating: rec.average_rating,
                    city: rec.city,
                    state: rec.state,
                    tags: rec.tags || [],
                    review: personalMessage,
                    // Include service/category info
                    recommended_service_name: rec.recommended_service_name || rec.service_type,
                    category_name: rec.category_name,
                    // Include personal experience details
                    rating: rec.rating || rec.initial_rating,
                    num_likes: rec.num_likes || 0
                };
            });

            // Add the current user's name to the API request
            const currentUserName = "Someone";
            
            // Determine if this is a mention query (searching for specific user)
            const isMentionQuery = isSpecificUserSearch();
            
            // Create appropriate instructions based on query type
            let instructions;
            if (isMentionQuery) {
                // For mention queries, don't include recommender names since they're redundant
                instructions = "Generate a concise summary: 'Here are the top recommendations:' then numbered items like '1) \"[Business Name]\" (4⭐) - [quote]'. Keep brief - just business name, rating, and quote. No recommender names, location, analysis, or extra commentary.";
            } else {
                // For general searches, include recommender names
                instructions = "Generate a concise summary: 'Here are the top recommendations:' then numbered items like '1) [Recommender Name] recommended \"[Business Name]\" (4⭐) - [quote]'. Include recommender name with each item. Keep brief - just who recommended what, rating, and quote. No location, analysis, or extra commentary.";
            }
            
            const response = await fetch(`${API_URL}/api/ai-summary`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    query,
                    recommendations: recsForAPI,
                    currentUserName: currentUserName,
                    instructions: instructions
                })
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            
            if (data.success) {
                setSummary(data.summary);
            } else {
                throw new Error(data.error || 'Failed to generate summary');
            }
        } catch (err) {
            console.error('Error generating AI summary:', err);
            setError('Failed to generate AI summary. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleRefresh = () => {
        setSummary('');
        setError(null);
        generateSummary();
    };

    if (!isVisible) return null;

    const photos = getPhotosForDisplay();
    const hasPhotos = photos.length > 0;

    return (
        <div className="ai-summary-container">
            <div className="ai-summary-content">
                {isLoading && (
                    <div className="ai-summary-loading">
                        <div className="loading-spinner"></div>
                        {/* Loading silently - no text */}
                    </div>
                )}

                {/* Error display hidden for cleaner UI */}
                {/* {error && (
                    <div className="ai-summary-error">
                        <p>{error}</p>
                        <button className="retry-button" onClick={handleRefresh}>
                            Try Again
                        </button>
                    </div>
                )} */}

                {summary && !isLoading && !error && (
                    <div className={`ai-summary-text ${hasPhotos ? 'with-photos' : ''}`}>
                        {/* Header - Only show for specific user searches (mentions) */}
                        {isSpecificUserSearch() && (
                            <div className="ai-summary-header">
                                <div className="ai-summary-users-photos">
                                    {(() => {
                                        const usersWithRecs = getUsersWithRecommendations();
                                        console.log('=== DEBUG: Rendering profile photos ===');
                                        console.log('mentionedUserProfilePhoto:', mentionedUserProfilePhoto);
                                        console.log('usersWithRecs:', usersWithRecs);
                                        
                                        if (usersWithRecs.length > 0) {
                                            if (usersWithRecs.length === 1) {
                                                const user = usersWithRecs[0];
                                                // For mention queries, prioritize the fetched profile photo
                                                const profilePhotoToUse = mentionedUserProfilePhoto || user.photo;
                                                console.log('=== DEBUG: Single user profile photo ===');
                                                console.log('User:', user);
                                                console.log('Profile photo to use:', profilePhotoToUse);
                                                console.log('Image src result:', getImageSrc(profilePhotoToUse));
                                                
                                                return (
                                                    <div className="ai-summary-user-photo-wrapper">
                                                        {profilePhotoToUse ? (
                                                            <img 
                                                                src={getImageSrc(profilePhotoToUse)} 
                                                                alt={`${user.name}'s profile`} 
                                                                className="ai-summary-user-photo"
                                                                onError={(e) => {
                                                                    console.error('=== DEBUG: Image failed to load ===');
                                                                    console.error('Image src:', e.target.src);
                                                                    console.error('Profile photo value:', profilePhotoToUse);
                                                                    // Hide the broken image and show initials instead
                                                                    e.target.style.display = 'none';
                                                                    e.target.nextElementSibling.style.display = 'flex';
                                                                }}
                                                                onLoad={() => {
                                                                    console.log('=== DEBUG: Image loaded successfully ===');
                                                                    console.log('Image src:', getImageSrc(profilePhotoToUse));
                                                                }}
                                                            />
                                                        ) : null}
                                                        {/* Always render initials as fallback */}
                                                        <div className="ai-summary-user-photo ai-summary-user-initials" style={{ display: profilePhotoToUse ? 'none' : 'flex' }}>
                                                            <span>{user.name.charAt(0).toUpperCase()}</span>
                                                        </div>
                                                    </div>
                                                );
                                            } else if (usersWithRecs.length === 2) {
                                                return (
                                                    <>
                                                        <div className="ai-summary-user-photo-wrapper">
                                                            {mentionedUserProfilePhoto ? (
                                                                <img 
                                                                    src={getImageSrc(mentionedUserProfilePhoto)} 
                                                                    alt={`${usersWithRecs[0].name}'s profile`} 
                                                                    className="ai-summary-user-photo"
                                                                    onError={(e) => {
                                                                        e.target.style.display = 'none';
                                                                        e.target.nextElementSibling.style.display = 'flex';
                                                                    }}
                                                                />
                                                            ) : usersWithRecs[0].photo ? (
                                                                <img 
                                                                    src={getImageSrc(usersWithRecs[0].photo)} 
                                                                    alt={`${usersWithRecs[0].name}'s profile`} 
                                                                    className="ai-summary-user-photo"
                                                                    onError={(e) => {
                                                                        e.target.style.display = 'none';
                                                                        e.target.nextElementSibling.style.display = 'flex';
                                                                    }}
                                                                />
                                                            ) : null}
                                                            <div className="ai-summary-user-photo ai-summary-user-initials" style={{ display: (mentionedUserProfilePhoto || usersWithRecs[0].photo) ? 'none' : 'flex' }}>
                                                                <span>{usersWithRecs[0].name.charAt(0).toUpperCase()}</span>
                                                            </div>
                                                        </div>
                                                        <div className="ai-summary-user-photo-wrapper">
                                                            {usersWithRecs[1].photo ? (
                                                                <img 
                                                                    src={getImageSrc(usersWithRecs[1].photo)} 
                                                                    alt={`${usersWithRecs[1].name}'s profile`} 
                                                                    className="ai-summary-user-photo"
                                                                    onError={(e) => {
                                                                        e.target.style.display = 'none';
                                                                        e.target.nextElementSibling.style.display = 'flex';
                                                                    }}
                                                                />
                                                            ) : null}
                                                            <div className="ai-summary-user-photo ai-summary-user-initials" style={{ display: usersWithRecs[1].photo ? 'none' : 'flex' }}>
                                                                <span>{usersWithRecs[1].name.charAt(0).toUpperCase()}</span>
                                                            </div>
                                                        </div>
                                                    </>
                                                );
                                            } else {
                                                // Show first 2 users for 3+ users
                                                return (
                                                    <>
                                                        <div className="ai-summary-user-photo-wrapper">
                                                            {mentionedUserProfilePhoto ? (
                                                                <img 
                                                                    src={getImageSrc(mentionedUserProfilePhoto)} 
                                                                    alt={`${usersWithRecs[0].name}'s profile`} 
                                                                    className="ai-summary-user-photo"
                                                                    onError={(e) => {
                                                                        e.target.style.display = 'none';
                                                                        e.target.nextElementSibling.style.display = 'flex';
                                                                    }}
                                                                />
                                                            ) : usersWithRecs[0].photo ? (
                                                                <img 
                                                                    src={getImageSrc(usersWithRecs[0].photo)} 
                                                                    alt={`${usersWithRecs[0].name}'s profile`} 
                                                                    className="ai-summary-user-photo"
                                                                    onError={(e) => {
                                                                        e.target.style.display = 'none';
                                                                        e.target.nextElementSibling.style.display = 'flex';
                                                                    }}
                                                                />
                                                            ) : null}
                                                            <div className="ai-summary-user-photo ai-summary-user-initials" style={{ display: (mentionedUserProfilePhoto || usersWithRecs[0].photo) ? 'none' : 'flex' }}>
                                                                <span>{usersWithRecs[0].name.charAt(0).toUpperCase()}</span>
                                                            </div>
                                                        </div>
                                                        <div className="ai-summary-user-photo-wrapper">
                                                            {usersWithRecs[1].photo ? (
                                                                <img 
                                                                    src={getImageSrc(usersWithRecs[1].photo)} 
                                                                    alt={`${usersWithRecs[1].name}'s profile`} 
                                                                    className="ai-summary-user-photo"
                                                                    onError={(e) => {
                                                                        e.target.style.display = 'none';
                                                                        e.target.nextElementSibling.style.display = 'flex';
                                                                    }}
                                                                />
                                                            ) : null}
                                                            <div className="ai-summary-user-photo ai-summary-user-initials" style={{ display: usersWithRecs[1].photo ? 'none' : 'flex' }}>
                                                                <span>{usersWithRecs[1].name.charAt(0).toUpperCase()}</span>
                                                            </div>
                                                        </div>
                                                    </>
                                                );
                                            }
                                        }
                                        return null;
                                    })()}
                                </div>
                                <div className="ai-summary-header-text">
                                    <span className="ai-summary-user-name">
                                        {(() => {
                                            const usersWithRecs = getUsersWithRecommendations();
                                            if (usersWithRecs.length > 0) {
                                                if (usersWithRecs.length === 1) {
                                                    return usersWithRecs[0].name;
                                                } else if (usersWithRecs.length === 2) {
                                                    return `${usersWithRecs[0].name} and ${usersWithRecs[1].name}`;
                                                } else {
                                                    return `${usersWithRecs[0].name} and ${usersWithRecs.length - 1} others`;
                                                }
                                            }
                                            return 'Someone';
                                        })()}
                                    </span>
                                    <span className="ai-summary-recommendations-count">
                                        {recommendations && recommendations.length > 0 
                                            ? ` has ${recommendations.length} recommendations for you`
                                            : ' have some recommendations for you'
                                        }
                                    </span>
                                </div>
                            </div>
                        )}
                        
                        {/* AI Summary Content */}
                        <div className="ai-summary-recommendations">
                            {/* Profile Photos Section - Above the text */}
                            {!isSpecificUserSearch() && recommendations && recommendations.length > 0 && (
                                <div className="ai-summary-profile-photos">
                                    <div className="ai-summary-profile-photos-title">
                                        People who recommended:
                                    </div>
                                    <div className="ai-summary-profile-photos-grid">
                                        {getUsersWithRecommendations().map((user, index) => (
                                            <div key={index} className="ai-summary-profile-photo-item">
                                                {user.photo ? (
                                                    <img 
                                                        src={getImageSrc(user.photo)} 
                                                        alt={`${user.name}'s profile`} 
                                                        className="ai-summary-profile-photo"
                                                        onError={(e) => {
                                                            e.target.style.display = 'none';
                                                            e.target.nextElementSibling.style.display = 'flex';
                                                        }}
                                                    />
                                                ) : null}
                                                <div 
                                                    className="ai-summary-profile-photo ai-summary-profile-initials" 
                                                    style={{ display: user.photo ? 'none' : 'flex' }}
                                                >
                                                    <span>{user.name.charAt(0).toUpperCase()}</span>
                                                </div>
                                                <div className="ai-summary-profile-photo-name">
                                                    {user.name}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            
                            {/* AI Summary Text */}
                            <div className="ai-summary-content-text">
                                {renderTextWithLinks(summary)}
                            </div>
                            
                            {/* Recommendation Images Section - Below the text */}
                            {!isSpecificUserSearch() && recommendations && recommendations.length > 0 && getPhotosForDisplay().length > 0 && (
                                <div className="ai-summary-recommendation-images">
                                    <div className="ai-summary-recommendation-images-title">
                                        Photos from recommendations:
                                    </div>
                                    <div className="ai-summary-recommendation-images-grid">
                                        {getPhotosForDisplay().map((photo, index) => (
                                            <div key={index} className="ai-summary-recommendation-image-item">
                                                <img 
                                                    src={photo.src} 
                                                    alt={photo.alt}
                                                    className="ai-summary-recommendation-image-photo"
                                                />
                                                <div className="ai-summary-recommendation-image-caption">
                                                    {photo.businessName}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AISummary; 